import { useCallback, useRef, useState } from "react";
import type { ReactNode } from "react";

import { ConfirmDialog } from "./ConfirmDialog";
import type { Nullable } from "../types";

type ConfirmOptions = {
  title?: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
};

/**
 * Promise-based wrapper around ConfirmDialog. Call `confirm(...)` to open the
 * dialog; it resolves to `true` when the user confirms and `false` when they
 * cancel or dismiss it. Render the returned `confirmDialog` element once.
 */
export const useConfirmDialog = () => {
  const [options, setOptions] = useState<Nullable<ConfirmOptions>>(null);
  const resolveRef = useRef<Nullable<(value: boolean) => void>>(null);

  const settle = useCallback((value: boolean) => {
    resolveRef.current?.(value);
    resolveRef.current = null;
    setOptions(null);
  }, []);

  const confirm = useCallback((opts: ConfirmOptions = {}) => {
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setOptions(opts);
    });
  }, []);

  const confirmDialog = options ? (
    <ConfirmDialog
      isOpen
      icon={options.icon}
      title={options.title}
      description={options.description}
      onConfirm={() => settle(true)}
      onClose={() => settle(false)}
    />
  ) : null;

  return { confirm, confirmDialog };
};
